import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import triviapic from './torah trivia.png';

// Static data (public folder):
//   /torah_trivia/index.json
//   /torah_trivia/<Book>/<Parsha>.json
//
// Question schema per item:
// {
//   id, book, parsha,
//   type: "mcq" | "fill" | "who" | "where" | "what",
//   difficulty: "easy" | "medium" | "hard" | "veryHard",
//   prompt, choices?: string[], answer: string, source?: string
// }
//
// Cloud leaderboard endpoint contract:
//   GET  /api/torah-trivia/leaderboard
//     -> [] OR { entries: [] }
//   POST /api/torah-trivia/leaderboard
//     body: { entry, proof, duplicateAction: 'insert' | 'replace' }
//     -> optional [] OR { entries: [] }

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.highpriest.net';
const BOOKS = ['Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy'];
const DIFFS = ['easy', 'medium', 'hard', 'veryHard'];

const CORRECT_TO_ADVANCE = 10;
const MAX_INCORRECT = 3;
const MAX_LEADERBOARD_ENTRIES = 10;
const LEADERBOARD_CACHE_KEY = 'torahTriviaLeaderboardEntriesCacheV2';
const CLOUD_LEADERBOARD_ENDPOINT = `${API_BASE}/api/torah-trivia/leaderboard`;
const CLOUD_SESSION_ENDPOINT = `${API_BASE}/api/torah-trivia/session`;
const MAX_PROOF_EVENTS = 500;

const PARASHOT_BY_BOOK = {
  Genesis: [
    'Bereshit',
    'Noach',
    'Lech-Lecha',
    'Vayera',
    'Chayei_Sarah',
    'Toldot',
    'Vayetzei',
    'Vayishlach',
    'Vayeshev',
    'Miketz',
    'Vayigash',
    'Vayechi',
  ],
  Exodus: [
    'Shemot',
    'Vaera',
    'Bo',
    'Beshalach',
    'Yitro',
    'Mishpatim',
    'Terumah',
    'Tetzaveh',
    'Ki_Tisa',
    'Vayakhel',
    'Pekudei',
  ],
  Leviticus: [
    'Vayikra',
    'Tzav',
    'Shemini',
    'Tazria',
    'Metzora',
    'Acharei_Mot',
    'Kedoshim',
    'Emor',
    'Behar',
    'Bechukotai',
  ],
  Numbers: [
    'Bamidbar',
    'Naso',
    'Behaalotecha',
    'Shelach',
    'Korach',
    'Chukat',
    'Balak',
    'Pinchas',
    'Matot',
    'Masei',
  ],
  Deuteronomy: [
    'Devarim',
    'Vaetchanan',
    'Eikev',
    'Reeh',
    'Shoftim',
    'Ki_Teitzei',
    'Ki_Tavo',
    'Nitzavim',
    'Vayelech',
    'Haazinu',
    'Vezot_Haberakhah',
  ],
};

const primaryButtonStyle = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #1f3b73',
  background: '#1f5cff',
  color: 'white',
  fontWeight: 900,
};

const secondaryButtonStyle = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid rgba(11,27,42,0.18)',
  background: 'white',
  fontWeight: 900,
};

const fieldStyle = {
  width: '100%',
  padding: 12,
  borderRadius: 12,
  border: '1px solid rgba(11,27,42,0.18)',
};

const focusRingStyle = {
  outline: '3px solid rgba(31,92,255,0.35)',
  outlineOffset: 2,
  boxShadow: '0 0 0 4px rgba(31,92,255,0.12)',
};

const FocusableButton = React.forwardRef(function FocusableButton(
  { variant = 'primary', children, disabled = false, style = {}, onFocus, onBlur, ...props },
  ref
) {
  const [isFocused, setIsFocused] = useState(false);
  const baseStyle = variant === 'primary' ? primaryButtonStyle : secondaryButtonStyle;

  return (
    <button
      {...props}
      ref={ref}
      disabled={disabled}
      onFocus={(e) => {
        setIsFocused(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        onBlur?.(e);
      }}
      style={{
        ...baseStyle,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.72 : 1,
        ...(isFocused && !disabled ? focusRingStyle : null),
        ...style,
      }}
    >
      {children}
    </button>
  );
});

const PrimaryButton = React.forwardRef(function PrimaryButton(props, ref) {
  return <FocusableButton ref={ref} variant="primary" {...props} />;
});

const SecondaryButton = React.forwardRef(function SecondaryButton(props, ref) {
  return <FocusableButton ref={ref} variant="secondary" {...props} />;
});

const FocusableInput = React.forwardRef(function FocusableInput(
  { style = {}, onFocus, onBlur, ...props },
  ref
) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <input
      {...props}
      ref={ref}
      onFocus={(e) => {
        setIsFocused(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        onBlur?.(e);
      }}
      style={{
        ...fieldStyle,
        ...(isFocused ? focusRingStyle : null),
        ...style,
      }}
    />
  );
});

const FocusableSelect = React.forwardRef(function FocusableSelect(
  { style = {}, onFocus, onBlur, ...props },
  ref
) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <select
      {...props}
      ref={ref}
      onFocus={(e) => {
        setIsFocused(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        onBlur?.(e);
      }}
      style={{
        ...(isFocused ? focusRingStyle : null),
        ...style,
      }}
    />
  );
});

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function shuffleQuestionChoicesForSession(question) {
  if (!question) return question;

  const isMcqLike =
    question.type === 'mcq' ||
    question.type === 'who' ||
    question.type === 'where' ||
    question.type === 'what' ||
    question.type === 'when' ||
    question.type === 'which' ||
    question.type === 'how' ||
    question.type === 'how many';

  if (!isMcqLike || !Array.isArray(question.choices) || question.choices.length < 2) {
    return question;
  }

  return {
    ...question,
    choices: shuffle(question.choices),
  };
}

function normalizeAnswer(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function sanitizeName(name) {
  return String(name || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40);
}

function normalizeNameKey(name) {
  return sanitizeName(name).toLowerCase();
}

function titleCaseDifficulty(diff) {
  if (diff === 'veryHard') return 'Very Hard';
  return String(diff || '').charAt(0).toUpperCase() + String(diff || '').slice(1);
}

function getDifficultyRank(diff) {
  return Math.max(0, DIFFS.indexOf(diff));
}

function createEmptyDiffBuckets() {
  return {
    easy: [],
    medium: [],
    hard: [],
    veryHard: [],
  };
}

function getScopeMeta(scope, book, parsha) {
  if (scope === 'parsha') {
    return {
      scopeType: 'parsha',
      scopeKey: `parsha:${book}:${parsha}`,
      scopeLabel: `${book} / ${parsha}`,
      book,
      parsha,
    };
  }

  if (scope === 'book') {
    return {
      scopeType: 'book',
      scopeKey: `book:${book}`,
      scopeLabel: book,
      book,
      parsha: '',
    };
  }

  return {
    scopeType: 'all',
    scopeKey: 'all:EntireTorah',
    scopeLabel: 'Entire Torah',
    book: '',
    parsha: '',
  };
}

function compareLeaderboardEntries(a, b) {
  if (b.score !== a.score) return b.score - a.score;

  if (getDifficultyRank(b.difficulty) !== getDifficultyRank(a.difficulty)) {
    return getDifficultyRank(b.difficulty) - getDifficultyRank(a.difficulty);
  }

  if (a.incorrect !== b.incorrect) return a.incorrect - b.incorrect;
  if (b.answered !== a.answered) return b.answered - a.answered;
  return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
}

function sortLeaderboard(entries) {
  return [...entries].sort(compareLeaderboardEntries).slice(0, MAX_LEADERBOARD_ENTRIES);
}

function getEntriesForScope(entries, scopeKey) {
  return sortLeaderboard((entries || []).filter((entry) => entry.scopeKey === scopeKey));
}

function qualifiesForLeaderboard(entries, candidate) {
  if (!candidate || candidate.score <= 0) return false;
  if (entries.length < MAX_LEADERBOARD_ENTRIES) return true;
  const ranked = [...entries, candidate].sort(compareLeaderboardEntries);
  return ranked.indexOf(candidate) < MAX_LEADERBOARD_ENTRIES;
}

function formatLeaderboardDate(value) {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '—';

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(dt);
}

function hydrateLeaderboardEntry(entry) {
  const scopeMeta = getScopeMeta(
    entry?.scopeType === 'book' || entry?.scopeType === 'parsha' ? entry.scopeType : 'all',
    entry?.book || 'Genesis',
    entry?.parsha || ''
  );
  const cleanedName = sanitizeName(entry?.name || 'Anonymous');

  return {
    id:
      entry?.id ||
      `${entry?.scopeKey || scopeMeta.scopeKey}__${normalizeNameKey(cleanedName)}__${
        entry?.createdAt || new Date().toISOString()
      }`,
    name: cleanedName,
    nameKey: entry?.nameKey || normalizeNameKey(cleanedName),
    score: Number(entry?.score || 0),
    incorrect: Number(entry?.incorrect || 0),
    answered: Number(entry?.answered || 0),
    difficulty: DIFFS.includes(entry?.difficulty) ? entry.difficulty : 'easy',
    scopeType: entry?.scopeType || scopeMeta.scopeType,
    scopeKey: entry?.scopeKey || scopeMeta.scopeKey,
    scopeLabel: entry?.scopeLabel || scopeMeta.scopeLabel,
    book: entry?.book ?? scopeMeta.book,
    parsha: entry?.parsha ?? scopeMeta.parsha,
    createdAt: entry?.createdAt || new Date().toISOString(),
    sessionId: entry?.sessionId || '',
  };
}

function hydrateLeaderboardEntries(payload) {
  const rawEntries = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.entries)
      ? payload.entries
      : [];
  return rawEntries.map(hydrateLeaderboardEntry);
}

function upsertLeaderboardEntry(entries, nextEntry) {
  const withoutSameName = (entries || []).filter(
    (entry) => !(entry.scopeKey === nextEntry.scopeKey && entry.nameKey === nextEntry.nameKey)
  );
  const sameScope = sortLeaderboard([
    ...withoutSameName.filter((entry) => entry.scopeKey === nextEntry.scopeKey),
    nextEntry,
  ]);
  const otherScopes = withoutSameName.filter((entry) => entry.scopeKey !== nextEntry.scopeKey);
  return [...otherScopes, ...sameScope];
}

function readLeaderboardCache() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LEADERBOARD_CACHE_KEY);
    if (!raw) return [];
    return hydrateLeaderboardEntries(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeLeaderboardCache(entries) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LEADERBOARD_CACHE_KEY, JSON.stringify(entries || []));
  } catch {
    // Ignore cache write failures.
  }
}

async function fetchJson(url) {
  const resp = await fetch(url, { cache: 'no-store' });
  if (!resp.ok) throw new Error(`Failed to fetch ${url} (HTTP ${resp.status})`);
  return resp.json();
}

async function fetchLeaderboardFromCloud() {
  const resp = await fetch(CLOUD_LEADERBOARD_ENDPOINT, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });

  if (!resp.ok) {
    throw new Error(`Cloud leaderboard fetch failed (HTTP ${resp.status})`);
  }

  const data = await resp.json();
  return hydrateLeaderboardEntries(data);
}

async function createCloudPlaySession(scopeMeta) {
  const resp = await fetch(CLOUD_SESSION_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      scopeType: scopeMeta.scopeType,
      book: scopeMeta.book,
      parsha: scopeMeta.parsha,
    }),
  });

  if (!resp.ok) {
    const data = await resp.json().catch(() => null);
    throw new Error(data?.error || `Cloud trivia session failed (HTTP ${resp.status})`);
  }

  return resp.json();
}

async function publishLeaderboardToCloud(entry, proof, duplicateAction) {
  const resp = await fetch(CLOUD_LEADERBOARD_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ entry, proof, duplicateAction }),
  });

  const text = await resp.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!resp.ok) {
    throw new Error(data?.error || `Cloud leaderboard publish failed (HTTP ${resp.status})`);
  }

  return data ? hydrateLeaderboardEntries(data) : null;
}

function validateQuestion(q) {
  if (!q || !q.id || !q.book || !q.parsha || !q.type || !q.difficulty || !q.prompt || !q.answer) {
    return false;
  }
  if (!DIFFS.includes(q.difficulty)) return false;

  const isFill = q.type === 'fill';
  const isMcqLike =
    q.type === 'mcq' ||
    q.type === 'who' ||
    q.type === 'where' ||
    q.type === 'what' ||
    q.type === 'when' ||
    q.type === 'which' ||
    q.type === 'how' ||
    q.type === 'how many';

  if (isFill) return true;
  if (isMcqLike) {
    return Array.isArray(q.choices) && q.choices.length >= 2 && q.choices.includes(q.answer);
  }
  return false;
}

function isLeaderboardMessageError(message) {
  return /failed|missing|not allowed|different name|incomplete|cannot|invalid|wait|already/i.test(
    String(message || '')
  );
}

function getStatusMessageColor(isError) {
  return isError ? '#b00020' : '#0b6b2f';
}

function StatusMessage({ id, message, isError = false, announce = 'off', marginTop = 12 }) {
  if (!message) return null;

  const liveProps =
    announce === 'assertive'
      ? { role: 'alert', 'aria-live': 'assertive', 'aria-atomic': 'true' }
      : announce === 'polite'
        ? { role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' }
        : {};

  return (
    <div
      id={id}
      {...liveProps}
      style={{ marginTop, color: getStatusMessageColor(isError), fontWeight: 800 }}
    >
      {message}
    </div>
  );
}

function ScopeSelectorControls({
  radioName,
  selectedScope,
  onScopeChange,
  book,
  onBookChange,
  parsha,
  onParshaChange,
  availableParashot,
  bookLabel = 'Book',
  parshaLabel = 'Parsha',
  disableBookWhenAll = true,
  disableParshaUnlessParsha = true,
  trailingContent = null,
}) {
  return (
    <>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontWeight: 900 }}>
          <input
            type="radio"
            name={radioName}
            checked={selectedScope === 'all'}
            onChange={() => onScopeChange('all')}
          />{' '}
          Entire Torah
        </label>
        <label style={{ fontWeight: 900 }}>
          <input
            type="radio"
            name={radioName}
            checked={selectedScope === 'book'}
            onChange={() => onScopeChange('book')}
          />{' '}
          Book
        </label>
        <label style={{ fontWeight: 900 }}>
          <input
            type="radio"
            name={radioName}
            checked={selectedScope === 'parsha'}
            onChange={() => onScopeChange('parsha')}
          />{' '}
          Parsha
        </label>
        {trailingContent}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: selectedScope === 'parsha' ? '1fr 1fr' : '1fr',
          gap: 10,
        }}
      >
        {selectedScope === 'book' || selectedScope === 'parsha' ? (
          <div>
            <div style={{ fontSize: 12, opacity: 0.8, fontWeight: 800 }}>{bookLabel}</div>
            <FocusableSelect
              value={book}
              onChange={(e) => onBookChange(e.target.value)}
              disabled={disableBookWhenAll && selectedScope === 'all'}
              style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid rgba(11,27,42,0.2)' }}
            >
              {BOOKS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </FocusableSelect>
          </div>
        ) : null}

        {selectedScope === 'parsha' ? (
          <div>
            <div style={{ fontSize: 12, opacity: 0.8, fontWeight: 800 }}>{parshaLabel}</div>
            <FocusableSelect
              value={parsha}
              onChange={(e) => onParshaChange(e.target.value)}
              disabled={disableParshaUnlessParsha && selectedScope !== 'parsha'}
              style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid rgba(11,27,42,0.2)' }}
            >
              {availableParashot.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </FocusableSelect>
          </div>
        ) : null}
      </div>
    </>
  );
}

function QuestionChoiceButton({ choice, onChoose, disabled }) {
  return (
    <SecondaryButton
      onClick={() => onChoose(choice)}
      disabled={disabled}
      style={{
        width: '100%',
        padding: 12,
        textAlign: 'left',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.7)',
        fontWeight: 800,
      }}
    >
      {choice}
    </SecondaryButton>
  );
}

function LeaderboardRow({ entry, index }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '72px minmax(160px, 1.3fr) 96px 140px 120px 160px',
        gap: 10,
        alignItems: 'center',
        padding: 12,
        borderRadius: 14,
        border: '1px solid rgba(11,27,42,0.12)',
        background: index === 0 ? 'rgba(255,215,0,0.12)' : 'rgba(255,255,255,0.75)',
      }}
    >
      <div style={{ fontWeight: 900, color: '#1f3b73' }}>#{index + 1}</div>
      <div style={{ fontWeight: 900, color: '#0b1b2a' }}>{entry.name}</div>
      <div style={{ fontWeight: 900 }}>{entry.score}</div>
      <div>{titleCaseDifficulty(entry.difficulty)}</div>
      <div>{formatLeaderboardDate(entry.createdAt)}</div>
      <div style={{ fontSize: 12, opacity: 0.78 }}>
        {entry.answered} answered • {entry.incorrect} wrong
      </div>
    </div>
  );
}

function LeaderboardPublishModal({
  isOpen,
  entry,
  leaderboardName,
  leaderboardMessage,
  publishingLeaderboard,
  onNameChange,
  onPublish,
  onSkip,
  onClose,
}) {
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const closeButtonRef = useRef(null);
  const didInitializeFocusRef = useRef(false);
  const modalTitleId = 'leaderboard-modal-title';
  const modalDescriptionId = 'leaderboard-modal-description';
  const modalMessageId = 'leaderboard-modal-message';
  const isMessageError = isLeaderboardMessageError(leaderboardMessage);

  useEffect(() => {
    if (!isOpen) {
      didInitializeFocusRef.current = false;
      return undefined;
    }

    const dialogEl = dialogRef.current;

    const focusInitialElement = () => {
      if (didInitializeFocusRef.current) return;
      didInitializeFocusRef.current = true;

      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
        return;
      }
      if (closeButtonRef.current) {
        closeButtonRef.current.focus();
        return;
      }
      if (dialogEl) dialogEl.focus();
    };

    focusInitialElement();

    const handleKeyDown = (e) => {
      if (!dialogEl) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;

      const focusable = Array.from(
        dialogEl.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((node) => !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true');

      if (!focusable.length) {
        e.preventDefault();
        dialogEl.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || active === dialogEl) {
          e.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !entry) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(11,27,42,0.62)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1000,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        aria-describedby={`${modalDescriptionId}${leaderboardMessage ? ` ${modalMessageId}` : ''}`}
        tabIndex={-1}
        style={{
          width: '100%',
          maxWidth: 540,
          background: 'white',
          borderRadius: 20,
          padding: 22,
          boxShadow: '0 20px 60px rgba(11,27,42,0.25)',
          border: '1px solid rgba(11,27,42,0.12)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div id={modalTitleId} style={{ fontWeight: 900, fontSize: 20, color: '#0b1b2a' }}>
              Top 10 leaderboard
            </div>
            <div id={modalDescriptionId} style={{ marginTop: 6, fontSize: 14, opacity: 0.88 }}>
              Your verified score qualifies for <b>{entry.scopeLabel}</b>.
            </div>
          </div>

          <SecondaryButton
            ref={closeButtonRef}
            onClick={onClose}
            disabled={publishingLeaderboard}
            style={{
              width: 40,
              height: 40,
              padding: 0,
              borderRadius: 999,
              fontSize: 24,
              lineHeight: 1,
              color: '#0b1b2a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: publishingLeaderboard ? 'none' : '0 4px 14px rgba(11,27,42,0.08)',
            }}
            aria-label="Close leaderboard popup"
            title="Close"
          >
            ×
          </SecondaryButton>
        </div>

        <div
          style={{
            marginTop: 14,
            padding: 14,
            borderRadius: 14,
            background: 'rgba(31,92,255,0.06)',
            border: '1px solid rgba(31,92,255,0.14)',
          }}
        >
          <div style={{ fontWeight: 800 }}>Score: {entry.score}</div>
          <div style={{ marginTop: 6, fontSize: 13, opacity: 0.82 }}>
            Difficulty: <b>{titleCaseDifficulty(entry.difficulty)}</b> • Wrong answers: <b>{entry.incorrect}</b> •
            Answered: <b>{entry.answered}</b>
          </div>
        </div>

        <div style={{ marginTop: 16, fontSize: 14, opacity: 0.92 }}>
          Enter your name to publish your score. Each leaderboard keeps one entry per name. If your new score is
          better, it replaces your older score for that scope.
        </div>

        <div style={{ marginTop: 14 }}>
          <FocusableInput
            ref={inputRef}
            value={leaderboardName}
            onChange={(e) => onNameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onPublish();
            }}
            aria-label="Leaderboard name"
            aria-describedby={`${modalDescriptionId}${leaderboardMessage ? ` ${modalMessageId}` : ''}`}
            aria-invalid={isMessageError ? 'true' : 'false'}
            placeholder="Your name"
            maxLength={40}
            style={{ padding: 14 }}
          />
        </div>

        <StatusMessage
          id={modalMessageId}
          message={leaderboardMessage}
          isError={isMessageError}
          announce={leaderboardMessage ? (isMessageError ? 'assertive' : 'polite') : 'off'}
        />

        <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <SecondaryButton onClick={onSkip} disabled={publishingLeaderboard}>
            Skip
          </SecondaryButton>
          <PrimaryButton onClick={onPublish} disabled={!leaderboardName.trim() || publishingLeaderboard}>
            {publishingLeaderboard ? 'Publishing…' : 'Publish score'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default function TorahTrivia() {
  const [scope, setScope] = useState('all');
  const [book, setBook] = useState('Genesis');
  const [parsha, setParsha] = useState(PARASHOT_BY_BOOK.Genesis[0]);

  const [leaderboardViewScope, setLeaderboardViewScope] = useState('all');
  const [leaderboardViewBook, setLeaderboardViewBook] = useState('Genesis');
  const [leaderboardViewParsha, setLeaderboardViewParsha] = useState(PARASHOT_BY_BOOK.Genesis[0]);

  const [index, setIndex] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [loadingIndex, setLoadingIndex] = useState(true);

  const cacheRef = useRef(new Map());
  const playDecksRef = useRef(new Map());
  const leaderboardPopupTriggerRef = useRef(null);
  const lastModalTriggerRef = useRef(null);

  const [session, setSession] = useState(null);
  const [typed, setTyped] = useState('');
  const [locked, setLocked] = useState(false);
  const [feedback, setFeedback] = useState('');

  const [leaderboardEntries, setLeaderboardEntries] = useState([]);
  const [pendingLeaderboardEntry, setPendingLeaderboardEntry] = useState(null);
  const [leaderboardName, setLeaderboardName] = useState('');
  const [leaderboardMessage, setLeaderboardMessage] = useState('');
  const [leaderboardSyncState, setLeaderboardSyncState] = useState('loading');
  const [leaderboardSyncMessage, setLeaderboardSyncMessage] = useState('Loading leaderboard…');
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);
  const [publishingLeaderboard, setPublishingLeaderboard] = useState(false);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [playSessionToken, setPlaySessionToken] = useState('');
  const [playSessionId, setPlaySessionId] = useState('');
  const [playSessionStartedAt, setPlaySessionStartedAt] = useState('');
  const [scoreProofEvents, setScoreProofEvents] = useState([]);

  const availableParashot = PARASHOT_BY_BOOK[book] || [];
  const leaderboardViewParashot = PARASHOT_BY_BOOK[leaderboardViewBook] || [];

  useEffect(() => {
    if (!availableParashot.includes(parsha)) setParsha(availableParashot[0] || '');
  }, [book, parsha, availableParashot]);

  useEffect(() => {
    if (!leaderboardViewParashot.includes(leaderboardViewParsha)) {
      setLeaderboardViewParsha(leaderboardViewParashot[0] || '');
    }
  }, [leaderboardViewBook, leaderboardViewParsha, leaderboardViewParashot]);

  useEffect(() => {
    writeLeaderboardCache(leaderboardEntries);
  }, [leaderboardEntries]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingIndex(true);
        setLoadErr('');
        const idx = await fetchJson('/torah_trivia/index.json');
        if (!cancelled) setIndex(idx);
      } catch (e) {
        if (!cancelled) setLoadErr(e?.message || 'Failed to load trivia index.');
      } finally {
        if (!cancelled) setLoadingIndex(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const cached = readLeaderboardCache();

      if (!cancelled && cached.length) {
        setLeaderboardEntries(cached);
        setLeaderboardSyncState('cache');
        setLeaderboardSyncMessage('Showing cached leaderboard while cloud sync loads.');
      }

      try {
        const cloudEntries = await fetchLeaderboardFromCloud();
        if (cancelled) return;

        setLeaderboardEntries(cloudEntries);
        setLeaderboardSyncState('cloud');
        setLeaderboardSyncMessage('Cloud leaderboard connected.');
      } catch {
        if (cancelled) return;

        const fallback = cached.length ? cached : [];
        setLeaderboardEntries(fallback);
        setLeaderboardSyncState(fallback.length ? 'cache' : 'offline');
        setLeaderboardSyncMessage(
          fallback.length
            ? 'Cloud leaderboard unavailable. Showing cached leaderboard from this browser.'
            : 'Cloud leaderboard unavailable. New scores will be cached locally until the API is live.'
        );
      } finally {
        if (!cancelled) setLeaderboardLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined' || !showLeaderboardModal) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [showLeaderboardModal]);

  const selectionPairs = useMemo(() => {
    if (scope === 'parsha') return [[book, parsha]];
    if (scope === 'book') return (PARASHOT_BY_BOOK[book] || []).map((p) => [book, p]);

    const pairs = [];
    for (const b of BOOKS) {
      for (const p of PARASHOT_BY_BOOK[b] || []) pairs.push([b, p]);
    }
    return pairs;
  }, [scope, book, parsha]);

  const totalQuestionsInSelection = useMemo(() => {
    if (!index?.counts) return 0;
    if (scope === 'parsha') return Number(index.counts?.[book]?.[parsha] || 0);

    if (scope === 'book') {
      let total = 0;
      for (const p of PARASHOT_BY_BOOK[book] || []) total += Number(index.counts?.[book]?.[p] || 0);
      return total;
    }

    let total = 0;
    for (const [b, p] of selectionPairs) total += Number(index.counts?.[b]?.[p] || 0);
    return total;
  }, [index, scope, book, parsha, selectionPairs]);

  const activeLeaderboardScopeMeta = useMemo(() => {
    return getScopeMeta(leaderboardViewScope, leaderboardViewBook, leaderboardViewParsha);
  }, [leaderboardViewScope, leaderboardViewBook, leaderboardViewParsha]);

  const activeLeaderboardEntries = useMemo(() => {
    return getEntriesForScope(leaderboardEntries, activeLeaderboardScopeMeta.scopeKey);
  }, [leaderboardEntries, activeLeaderboardScopeMeta]);

  async function refreshLeaderboard() {
    setLeaderboardLoading(true);
    setLeaderboardSyncState('loading');
    setLeaderboardSyncMessage('Refreshing leaderboard…');

    try {
      const cloudEntries = await fetchLeaderboardFromCloud();
      setLeaderboardEntries(cloudEntries);
      setLeaderboardSyncState('cloud');
      setLeaderboardSyncMessage('Cloud leaderboard refreshed.');
    } catch {
      const cached = readLeaderboardCache();
      setLeaderboardEntries(cached);
      setLeaderboardSyncState(cached.length ? 'cache' : 'offline');
      setLeaderboardSyncMessage(
        cached.length
          ? 'Refresh failed. Showing cached leaderboard from this browser.'
          : 'Refresh failed. Cloud leaderboard is still unavailable.'
      );
    } finally {
      setLeaderboardLoading(false);
    }
  }

  const openLeaderboardModal = useCallback((triggerEl) => {
    if (triggerEl) lastModalTriggerRef.current = triggerEl;
    setShowLeaderboardModal(true);
  }, []);

  const closeLeaderboardModal = useCallback(() => {
    if (publishingLeaderboard) return;
    setShowLeaderboardModal(false);
    const focusTarget = lastModalTriggerRef.current || leaderboardPopupTriggerRef.current;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      window.setTimeout(() => focusTarget.focus(), 0);
    }
  }, [publishingLeaderboard]);

  function clearPendingLeaderboardFlow() {
    setPendingLeaderboardEntry(null);
    setLeaderboardName('');
    setShowLeaderboardModal(false);
  }

  function resetPlaySessionProof() {
    setPlaySessionToken('');
    setPlaySessionId('');
    setPlaySessionStartedAt('');
    setScoreProofEvents([]);
  }

  function resetPlayDecks() {
    playDecksRef.current = new Map();
  }

  function appendProofEvent(event) {
    setScoreProofEvents((prev) => [...prev, event].slice(-MAX_PROOF_EVENTS));
  }

  function clearLowerTierQuestions(unlockedDiff) {
    for (const packed of playDecksRef.current.values()) {
      if (unlockedDiff === 'medium') packed.byDiff.easy = [];
      if (unlockedDiff === 'hard') {
        packed.byDiff.easy = [];
        packed.byDiff.medium = [];
      }
      if (unlockedDiff === 'veryHard') {
        packed.byDiff.easy = [];
        packed.byDiff.medium = [];
        packed.byDiff.hard = [];
      }
    }
  }

  function buildLeaderboardCandidate(s) {
    return {
      id: `${s.selectionScopeKey}__pending__${Date.now()}`,
      name: '',
      nameKey: '',
      score: s.score,
      incorrect: s.incorrect,
      answered: s.answered,
      difficulty: DIFFS[s.stage],
      scopeType: s.selectionScopeType,
      scopeKey: s.selectionScopeKey,
      scopeLabel: s.selectionLabel,
      book: s.selectionBook,
      parsha: s.selectionParsha,
      createdAt: new Date().toISOString(),
    };
  }

  function endSession(s, reason) {
    s.ended = true;
    s.endReason = reason;
    s.current = null;
    return s;
  }

  function finalizeSession(nextSession, reason) {
    const endedSession = { ...endSession(nextSession, reason) };
    const candidate = buildLeaderboardCandidate(endedSession);
    const scopeEntries = getEntriesForScope(leaderboardEntries, candidate.scopeKey);

    if (qualifiesForLeaderboard(scopeEntries, candidate)) {
      setPendingLeaderboardEntry(candidate);
      setLeaderboardName('');
      setLeaderboardMessage('');
      openLeaderboardModal(leaderboardPopupTriggerRef.current);
    } else {
      clearPendingLeaderboardFlow();
    }

    setLocked(false);
    setSession(endedSession);
  }

  async function loadPairSource(b, p) {
    const key = `${b}__${p}`;
    const cached = cacheRef.current.get(key);
    if (cached) return cached;

    const url = index?.files?.[b]?.[p];
    if (!url) throw new Error(`Missing file path for ${b}/${p} in index.json`);

    const data = await fetchJson(url);
    const qs = Array.isArray(data?.questions) ? data.questions.filter(validateQuestion) : [];

    const byDiff = createEmptyDiffBuckets();
    for (const q of qs) byDiff[q.difficulty].push(q);

    const packed = { byDiff };
    cacheRef.current.set(key, packed);
    return packed;
  }

  async function getPlayDeck(b, p) {
    const key = `${b}__${p}`;
    const existing = playDecksRef.current.get(key);
    if (existing) return existing;

    const source = await loadPairSource(b, p);
    const packed = {
      byDiff: {
        easy: shuffle(source.byDiff.easy.map(shuffleQuestionChoicesForSession)),
        medium: shuffle(source.byDiff.medium.map(shuffleQuestionChoicesForSession)),
        hard: shuffle(source.byDiff.hard.map(shuffleQuestionChoicesForSession)),
        veryHard: shuffle(source.byDiff.veryHard.map(shuffleQuestionChoicesForSession)),
      },
    };

    playDecksRef.current.set(key, packed);
    return packed;
  }

  async function pickNextQuestion(nextSession) {
    const diff = DIFFS[nextSession.stage];
    const order = nextSession.pairOrder;

    for (let i = 0; i < order.length; i += 1) {
      const [b, p] = order[(nextSession.pairCursor + i) % order.length];
      const bank = await getPlayDeck(b, p);
      const arr = bank.byDiff[diff];

      while (arr.length) {
        const candidate = arr.pop();
        if (!candidate) break;
        if (nextSession.askedIds.has(candidate.id)) continue;
        nextSession.askedIds.add(candidate.id);
        nextSession.pairCursor = (nextSession.pairCursor + i) % order.length;
        return candidate;
      }
    }

    return null;
  }

  async function startGame() {
    if (!index) {
      setFeedback('Trivia index not loaded yet.');
      return;
    }

    const scopeMeta = getScopeMeta(scope, book, parsha);

    clearPendingLeaderboardFlow();
    resetPlaySessionProof();
    resetPlayDecks();
    setLeaderboardMessage('');

    const pairOrder = shuffle(selectionPairs);
    const s = {
      stage: 0,
      correctInStage: 0,
      score: 0,
      incorrect: 0,
      answered: 0,
      askedIds: new Set(),
      pairOrder,
      pairCursor: 0,
      current: null,
      ended: false,
      endReason: '',
      selectionLabel: scopeMeta.scopeLabel,
      selectionScopeType: scopeMeta.scopeType,
      selectionScopeKey: scopeMeta.scopeKey,
      selectionBook: scopeMeta.book,
      selectionParsha: scopeMeta.parsha,
    };

    setFeedback('');
    setLocked(false);
    setTyped('');

    let sessionWarning = '';

    try {
      const localStartedAt = new Date().toISOString();
      const playSession = await createCloudPlaySession(scopeMeta);
      setPlaySessionToken(playSession.sessionToken || '');
      setPlaySessionId(playSession.sessionId || '');
      setPlaySessionStartedAt(playSession.startedAt || localStartedAt);
      setScoreProofEvents([]);
    } catch (e) {
      resetPlaySessionProof();
      sessionWarning =
        (e?.message || 'Live score verification is unavailable right now.') +
        ' You can still play, but this session cannot publish a verified leaderboard score.';
    }

    try {
      const first = await pickNextQuestion(s);
      if (!first) {
        s.ended = true;
        s.endReason = 'No questions available for this selection at the starting difficulty.';
      } else {
        s.current = first;
      }
      setFeedback(sessionWarning);
      setSession({ ...s, askedIds: s.askedIds });
    } catch (e) {
      resetPlaySessionProof();
      setFeedback(e?.message || 'Failed to start game.');
    }
  }

  async function answerMcq(choice) {
    if (!session || session.ended || locked || !session.current) return;
    setLocked(true);

    const q = session.current;
    const isCorrect = String(choice) === String(q.answer);

    appendProofEvent({
      questionId: q.id,
      answer: choice,
      stage: session.stage,
      difficulty: DIFFS[session.stage],
      answeredAt: new Date().toISOString(),
    });

    const s = { ...session, askedIds: session.askedIds };
    s.answered += 1;

    if (isCorrect) {
      s.score += 1;
      s.correctInStage += 1;
      setFeedback('Correct.');

      if (s.stage < 3 && s.correctInStage >= CORRECT_TO_ADVANCE) {
        s.stage += 1;
        s.correctInStage = 0;
        clearLowerTierQuestions(DIFFS[s.stage]);
        s.pairOrder = shuffle(s.pairOrder);
        s.pairCursor = 0;
      }
    } else {
      s.incorrect += 1;
      setFeedback(`Incorrect. Answer: ${q.answer}`);
      if (s.incorrect >= MAX_INCORRECT) {
        finalizeSession(s, 'Game over: 3 incorrect answers.');
        return;
      }
    }

    const next = await pickNextQuestion(s);
    if (!next) {
      finalizeSession(s, 'No more questions available at your current difficulty.');
      return;
    }

    s.current = next;
    setTyped('');
    setLocked(false);
    setSession({ ...s, askedIds: s.askedIds });
  }

  async function answerFill() {
    if (!session || session.ended || locked || !session.current) return;
    setLocked(true);

    const q = session.current;
    const submittedAnswer = typed;
    const isCorrect = normalizeAnswer(submittedAnswer) === normalizeAnswer(q.answer);

    appendProofEvent({
      questionId: q.id,
      answer: submittedAnswer,
      stage: session.stage,
      difficulty: DIFFS[session.stage],
      answeredAt: new Date().toISOString(),
    });

    const s = { ...session, askedIds: session.askedIds };
    s.answered += 1;

    if (isCorrect) {
      s.score += 1;
      s.correctInStage += 1;
      setFeedback('Correct.');

      if (s.stage < 3 && s.correctInStage >= CORRECT_TO_ADVANCE) {
        s.stage += 1;
        s.correctInStage = 0;
        clearLowerTierQuestions(DIFFS[s.stage]);
        s.pairOrder = shuffle(s.pairOrder);
        s.pairCursor = 0;
      }
    } else {
      s.incorrect += 1;
      setFeedback(`Incorrect. Answer: ${q.answer}`);
      if (s.incorrect >= MAX_INCORRECT) {
        finalizeSession(s, 'Game over: 3 incorrect answers.');
        return;
      }
    }

    const next = await pickNextQuestion(s);
    if (!next) {
      finalizeSession(s, 'No more questions available at your current difficulty.');
      return;
    }

    s.current = next;
    setTyped('');
    setLocked(false);
    setSession({ ...s, askedIds: s.askedIds });
  }

  function endCurrentSession() {
    if (!session || session.ended) return;
    const s = { ...session, askedIds: session.askedIds };
    finalizeSession(s, 'Session ended by player.');
  }

  async function publishLeaderboardEntry() {
    if (!pendingLeaderboardEntry || publishingLeaderboard) return;

    const cleanName = sanitizeName(leaderboardName);
    if (!cleanName) {
      setLeaderboardMessage('Please enter a name to publish your score.');
      return;
    }
    if (!playSessionToken || !playSessionId) {
      setLeaderboardMessage(
        'This score cannot be published because the signed play session is missing. Please start a new verified game.'
      );
      return;
    }
    if (!scoreProofEvents.length) {
      setLeaderboardMessage(
        'This score cannot be published because the signed score proof is incomplete. Please start a new verified game.'
      );
      return;
    }

    const entry = hydrateLeaderboardEntry({
      ...pendingLeaderboardEntry,
      sessionId: playSessionId,
      name: cleanName,
      nameKey: normalizeNameKey(cleanName),
    });

    const scopeEntries = getEntriesForScope(leaderboardEntries, entry.scopeKey);
    const duplicate = scopeEntries.find((item) => item.nameKey === entry.nameKey);

    if (duplicate && compareLeaderboardEntries(entry, duplicate) >= 0) {
      setLeaderboardMessage(
        'That name already appears on this leaderboard. Use a different name, or beat your existing score to replace it.'
      );
      return;
    }

    const proof = {
      sessionToken: playSessionToken,
      startedAt: playSessionStartedAt,
      finishedAt: new Date().toISOString(),
      events: scoreProofEvents,
    };

    const nextEntries = upsertLeaderboardEntry(leaderboardEntries, entry);
    const duplicateAction = duplicate ? 'replace' : 'insert';

    setPublishingLeaderboard(true);

    try {
      const cloudEntries = await publishLeaderboardToCloud(entry, proof, duplicateAction);
      setLeaderboardEntries(cloudEntries?.length ? cloudEntries : nextEntries);
      setLeaderboardSyncState('cloud');
      setLeaderboardSyncMessage('Cloud leaderboard updated.');
      setLeaderboardMessage(
        duplicate
          ? 'Your previous score was replaced with your new higher score.'
          : 'Your score has been published to the Top 10 leaderboard.'
      );
      clearPendingLeaderboardFlow();
    } catch (e) {
      setLeaderboardMessage(e?.message || 'Failed to publish leaderboard entry.');
    } finally {
      setPublishingLeaderboard(false);
    }
  }

  function skipLeaderboardEntry() {
    clearPendingLeaderboardFlow();
    setLeaderboardMessage('Your score qualified for the Top 10, but it was not published.');
  }

  function reset() {
    setSession(null);
    setTyped('');
    setLocked(false);
    setFeedback('');
    resetPlaySessionProof();
    resetPlayDecks();
  }

  const q = session?.current || null;
  const diffName = session ? DIFFS[session.stage] : '';

  return (
    <div className="mainInner">
      <div className="puzzleHeader">
        <div className="puzzleHeaderSpacer" />
        <h2 className="puzzleTitle">Torah Trivia</h2>
        <div className="puzzleHeaderAction">
          <Link to="/Game" className="puzzleBackButton">
            Back to Games
          </Link>
        </div>
      </div>

      <img src={triviapic} alt="trivia" className="heroImage" />

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
        <div className="heroCard" style={{ gridTemplateColumns: '1fr', maxWidth: 900 }}>
          {loadingIndex ? (
            <div style={{ fontWeight: 800 }}>Loading…</div>
          ) : loadErr ? (
            <div style={{ color: '#b00020', fontWeight: 900 }}>
              {loadErr}
              <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9, fontWeight: 700 }}>
                Ensure <code>public/torah_trivia/index.json</code> exists.
              </div>
            </div>
          ) : !session ? (
            <>
              <div style={{ color: '#0b1b2a', opacity: 0.95 }}>
                Difficulty increases after <b>{CORRECT_TO_ADVANCE}</b> correct answers in the current tier. The game
                ends after <b>{MAX_INCORRECT}</b> incorrect answers.
              </div>

              <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                <ScopeSelectorControls
                  radioName="scope"
                  selectedScope={scope}
                  onScopeChange={setScope}
                  book={book}
                  onBookChange={setBook}
                  parsha={parsha}
                  onParshaChange={setParsha}
                  availableParashot={availableParashot}
                />

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <PrimaryButton onClick={startGame}>Start</PrimaryButton>
                  <div style={{ fontSize: 13, opacity: 0.85 }}>
                    Questions in selection: <b>{totalQuestionsInSelection.toLocaleString()}</b>
                  </div>
                </div>

                <StatusMessage message={feedback} isError announce="assertive" marginTop={0} />
                <StatusMessage
                  message={!showLeaderboardModal ? leaderboardMessage : ''}
                  isError={isLeaderboardMessageError(leaderboardMessage)}
                  announce="polite"
                  marginTop={0}
                />
              </div>
            </>
          ) : session.ended ? (
            <>
              <div style={{ fontWeight: 900, fontSize: 18 }}>Session ended</div>
              <div style={{ opacity: 0.9 }}>{session.endReason}</div>
              <div style={{ marginTop: 10, fontWeight: 900 }}>
                Score: {session.score} correct • Incorrect: {session.incorrect} • Answered: {session.answered}
              </div>
              <div style={{ marginTop: 6, fontSize: 13, opacity: 0.82 }}>
                Final difficulty reached: <b>{titleCaseDifficulty(DIFFS[session.stage])}</b> • Selection:{' '}
                <b>{session.selectionLabel}</b>
              </div>

              {pendingLeaderboardEntry ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: 14,
                    borderRadius: 14,
                    background: 'rgba(31,92,255,0.08)',
                    border: '1px solid rgba(31,92,255,0.2)',
                  }}
                >
                  <div style={{ fontWeight: 900, color: '#0b1b2a' }}>
                    You made the Top 10 leaderboard for {pendingLeaderboardEntry.scopeLabel}.
                  </div>
                  <div style={{ marginTop: 6, fontSize: 14, opacity: 0.92 }}>
                    Use the popup to enter your name and publish your verified score.
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <PrimaryButton
                      ref={leaderboardPopupTriggerRef}
                      onClick={(e) => openLeaderboardModal(e.currentTarget)}
                    >
                      Open leaderboard popup
                    </PrimaryButton>
                    <SecondaryButton onClick={skipLeaderboardEntry} disabled={publishingLeaderboard}>
                      Skip
                    </SecondaryButton>
                  </div>
                </div>
              ) : null}

              <StatusMessage
                message={!showLeaderboardModal ? leaderboardMessage : ''}
                isError={isLeaderboardMessageError(leaderboardMessage)}
                announce="polite"
              />

              {!pendingLeaderboardEntry ? (
                <SecondaryButton onClick={reset} style={{ marginTop: 12 }}>
                  New session
                </SecondaryButton>
              ) : null}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 900 }}>Score: {session.score}</div>
                <div style={{ fontWeight: 900 }}>Lives: {MAX_INCORRECT - session.incorrect}</div>
                <div style={{ opacity: 0.9 }}>
                  Difficulty: <b>{diffName}</b> (correct in tier: {session.correctInStage}/{CORRECT_TO_ADVANCE})
                </div>
              </div>

              {q ? (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: '#0b1b2a' }}>{q.prompt}</div>
                  <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                    {q.book} • {q.parsha}
                    {q.source ? ` • ${q.source}` : ''}
                  </div>

                  {q.type === 'fill' ? (
                    <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <FocusableInput
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !locked) answerFill();
                        }}
                        disabled={locked}
                        placeholder="Type your answer"
                        style={{ flex: '1 1 260px' }}
                      />
                      <PrimaryButton onClick={answerFill} disabled={locked}>
                        Submit
                      </PrimaryButton>
                    </div>
                  ) : (
                    <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                      {q.choices.map((c) => (
                        <QuestionChoiceButton key={c} choice={c} onChoose={answerMcq} disabled={locked} />
                      ))}
                    </div>
                  )}

                  <StatusMessage
                    message={feedback}
                    isError={feedback ? !feedback.startsWith('Correct') : false}
                    announce="assertive"
                  />

                  <SecondaryButton onClick={endCurrentSession} style={{ marginTop: 14 }}>
                    End session
                  </SecondaryButton>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, marginBottom: 24 }}>
        <div className="heroCard" style={{ gridTemplateColumns: '1fr', maxWidth: 900, width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 18, color: '#0b1b2a' }}>Top 10 Leaderboards</div>
              <div style={{ marginTop: 4, fontSize: 13, opacity: 0.78 }}>
                Separate Top 10 boards for Entire Torah, each Book, and each Parsha.
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  color:
                    leaderboardSyncState === 'cloud'
                      ? '#0b6b2f'
                      : leaderboardSyncState === 'loading'
                        ? '#1f5cff'
                        : '#b26a00',
                }}
              >
                {leaderboardSyncState === 'cloud'
                  ? 'Cloud synced'
                  : leaderboardSyncState === 'loading'
                    ? 'Loading'
                    : leaderboardSyncState === 'cache'
                      ? 'Local cache fallback'
                      : 'Offline fallback'}
              </div>
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.72 }}>{leaderboardSyncMessage}</div>
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
            <ScopeSelectorControls
              radioName="leaderboardScope"
              selectedScope={leaderboardViewScope}
              onScopeChange={setLeaderboardViewScope}
              book={leaderboardViewBook}
              onBookChange={setLeaderboardViewBook}
              parsha={leaderboardViewParsha}
              onParshaChange={setLeaderboardViewParsha}
              availableParashot={leaderboardViewParashot}
              trailingContent={
                <SecondaryButton
                  onClick={refreshLeaderboard}
                  disabled={leaderboardLoading}
                  style={{ marginLeft: 'auto', padding: '8px 12px' }}
                >
                  {leaderboardLoading ? 'Refreshing…' : 'Refresh'}
                </SecondaryButton>
              }
            />
          </div>

          <div style={{ marginTop: 14, fontWeight: 900, color: '#0b1b2a' }}>
            Showing Top 10 for: {activeLeaderboardScopeMeta.scopeLabel}
          </div>

          {activeLeaderboardEntries.length === 0 ? (
            <div style={{ marginTop: 12, opacity: 0.8 }}>No scores have been published yet for this leaderboard.</div>
          ) : (
            <div style={{ marginTop: 12, overflowX: 'auto' }}>
              <div style={{ minWidth: 760 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '72px minmax(160px, 1.3fr) 96px 140px 120px 160px',
                    gap: 10,
                    padding: '0 12px 10px',
                    fontSize: 12,
                    fontWeight: 900,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    opacity: 0.7,
                  }}
                >
                  <div>Rank</div>
                  <div>Name</div>
                  <div>Score</div>
                  <div>Difficulty</div>
                  <div>Date</div>
                  <div>Stats</div>
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  {activeLeaderboardEntries.map((entry, i) => (
                    <LeaderboardRow key={`${entry.scopeKey}-${entry.nameKey}-${entry.createdAt}-${i}`} entry={entry} index={i} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <LeaderboardPublishModal
        isOpen={showLeaderboardModal}
        entry={pendingLeaderboardEntry}
        leaderboardName={leaderboardName}
        leaderboardMessage={leaderboardMessage}
        publishingLeaderboard={publishingLeaderboard}
        onNameChange={setLeaderboardName}
        onPublish={publishLeaderboardEntry}
        onSkip={skipLeaderboardEntry}
        onClose={closeLeaderboardModal}
      />
    </div>
  );
}